import {
  ref,
  toRef,
  toRefs,
  computed,
  defineComponent,
  watch,
  reactive,
  onMounted,
  inject,
  onUnmounted,
  provide,
  readonly,
} from "vue"

// candidate for reuse

import { msalInstance, loginRequest, getSilentRequest } from "../authConfig"
import { startLoading, stopLoading } from "../components/Notification"

const _authState = reactive({
  isAuthenticated: false,
  user: null,
})

export const authState = readonly(_authState)

export const isAuthenticated = computed(() => _authState.isAuthenticated)

export const user = computed(() => _authState.user)

export const role = computed(() => _authState.user?.idTokenClaims?.AppRole)

export const sub = computed(() => _authState.user?.idTokenClaims?.sub)

export const isAdmin = computed(() => _authState.user?.idTokenClaims?.AppRole === "Administrator")

export const showMessageToWorker = computed(() => {
  return isAdmin.value || ["Leader", "Worker"].some((r) => role.value?.startsWith(r))
})

export const isAuthorized = computed(() => !!role.value && role.value !== "None")

export const currentWorkerId = computed(() => _authState.user?.idTokenClaims?.WorkerID)

export const currentCustomerId = computed(() => _authState.user?.idTokenClaims?.CustomerID)

export function useAuth() {
  return {
    initialize,
    login,
    logout,
    handleRedirect,
    getTokenAsync,
    fetchAuthAsync,
  }
}

// Move function definitions to module scope so there's only one shared instance.
export const initialize = async () => {
  try {
    await msalInstance.initialize()
  } catch (error) {
    console.log("Initialization error", error)
  }
}

export const login = async () => {
  try {
    if (!msalInstance) {
      throw new Error("MSAL not initialized. Call initializeMsal() before using MSAL API.")
    }
    await msalInstance.loginRedirect(loginRequest)
    _authState.isAuthenticated = true
  } catch (error) {
    console.error("Login error:", error)
  }
}

export const logout = () => {
  if (!msalInstance) {
    throw new Error("MSAL not initialized. Call initializeMsal() before using MSAL API.")
  }
  msalInstance.logoutRedirect()
  _authState.isAuthenticated = false
  _authState.user = null
}

export const handleRedirect = async () => {
  try {
    const response = await msalInstance.handleRedirectPromise()
    const accounts = msalInstance.getAllAccounts()

    if (accounts.length > 0) {
      _authState.isAuthenticated = true

      // If we have a response from redirect (fresh login), use it
      if (response && response.account) {
        _authState.user = response.account
      } else {
        // On page refresh, we need to get a fresh token to access idTokenClaims
        try {
          const silentRequest = getSilentRequest(accounts[0])
          const tokenResponse = await msalInstance.acquireTokenSilent(silentRequest)
          _authState.user = tokenResponse.account
        } catch (error) {
          console.warn("Could not acquire token silently, using basic account info:", error)
          _authState.user = accounts[0]
        }
      }
    } else {
      _authState.isAuthenticated = false
      _authState.user = null
    }
  } catch (error) {
    console.error("Redirect error:", error)
  }
}

export const getTokenAsync = async () => {
  if (!msalInstance) {
    throw new Error("MSAL not initialized. Call initializeMsal() before using MSAL API.")
  }
  try {
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length === 0) {
      throw new Error("No accounts found. Please login first.")
    }
    const silentRequest = getSilentRequest(accounts[0])
    const response = await msalInstance.acquireTokenSilent(silentRequest)
    return response.accessToken
  } catch (error) {
    console.error("Silent token acquisition error:", error)
  }
}

let _writeId = 0

// every write (non-GET) shows the loading overlay until it finishes, so a save button can't be
// clicked twice on a slow connection; background refreshes are GETs and don't block the page
export const fetchAuthAsync = async (url, method = "GET", body = null) => {
  if (method === "GET") {
    return fetchAuthInnerAsync(url, method, body)
  }
  const subsystem = `write-${++_writeId}`
  startLoading(subsystem)
  try {
    return await fetchAuthInnerAsync(url, method, body)
  } finally {
    stopLoading(subsystem)
  }
}

const fetchAuthInnerAsync = async (url, method, body) => {
  const token = await getTokenAsync()
  if (!token) {
    const err = new Error(`No access token.`)
    err.url = `${method} ${url} `
    throw err
  }
  const headers = new Headers()

  const bearer = `Bearer ${token}`
  headers.append("Authorization", bearer)

  const options = {
    method,
    headers: headers,
  }

  if (body instanceof FormData) {
    options.body = body
  } else if (body) {
    headers.append("Content-Type", "application/json")
    options.body = JSON.stringify(body)
  }

  try {
    const response = await fetch(url, options)

    // If server responds with 403, mark user as unauthenticated and clear local accounts
    if (response.status === 403) {
      _authState.isAuthenticated = false
      _authState.user = null
      try {
        if (msalInstance && typeof msalInstance.getAllAccounts === "function") {
          const accounts = msalInstance.getAllAccounts()
          if (accounts && accounts.length) {
            if (typeof msalInstance.removeAccount === "function") {
              accounts.forEach((acct) => {
                try {
                  msalInstance.removeAccount(acct)
                } catch (err) {
                  console.warn("msal.removeAccount failed:", err)
                }
              })
            } else {
              // Fallback: remove MSAL-related entries from localStorage (cacheLocation is localStorage)
              for (let i = localStorage.length - 1; i >= 0; i--) {
                const key = localStorage.key(i)
                if (key && key.toLowerCase().includes("msal")) {
                  localStorage.removeItem(key)
                }
              }
            }
          }
        }
      } catch (err) {
        console.warn("Error clearing MSAL accounts:", err)
      }

      const err = new Error("Unauthorized (403)")
      err.url = `${method} ${url}`
      err.status = 403
      console.warn(err)
      throw err
    }
    // end 403 handling

    const contentType = response.headers.get("content-type")
    if (!response.headers || !contentType) {
      console.log(response)
      throw Error(`No headers in response: ${url}`)
    } else if (contentType.includes("application/json")) {
      if (!response.ok) {
        const body = await response.json()
        const err = new Error(body?.message || `Request failed (${response.status})`)
        err.url = url
        err.status = response.status
        throw err
      }
      return await response.json()
    } else if (contentType.includes("text/plain")) {
      if (!response.ok) {
        const text = await response.text()
        throw new Error(`Request failed (${response.status}): ${text.slice(0, 200)}`)
      }
      return await response.text()
    } else if (response.ok) {
      const blob = await response.blob()
      const file = window.URL.createObjectURL(blob)
      window.location.assign(file)
      return true
    } else {
      const text = await response.text()
      throw new Error(`Request failed (${response.status}) with content-type ${contentType}: ${text.slice(0, 200)}`)
    }
  } catch (e) {
    e.url = url
    console.log(e)
    throw e
  }
}
